import { useAuth } from '@/contexts/auth-context';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import type { ApiResponse } from '@fluenceur/types';
import api from '@/libs/axiosInstance';
import {
  isTerminalImportStatus,
  type ImportBatchProgress,
} from '@/stores/affiliate-csv-import-store';

export const affiliateCsvImportBatchQueryKey = (
  programId: string,
  batchId: string,
  brandId: string | null | undefined
) => ['affiliate-csv-import-batch', brandId, programId, batchId] as const;

interface UseAffiliateCsvImportBatchParams {
  programId: string;
  batchId: string;
  enabled?: boolean;
}

/**
 * Result of GET /dashboard/programs/:programId/affiliate-imports/:batchId.
 * `notFound: true` means the batch is gone (no persistence row) and the UI must
 * surface an "this import has expired" state instead of polling forever. Pusher
 * does not replay events, so without server state we cannot rehydrate progress.
 */
export type ImportBatchFetchResult =
  | { notFound: false; progress: ImportBatchProgress }
  | { notFound: true };

/**
 * Consecutive fetch failures tolerated before polling stops. A durable outage
 * would otherwise keep a hidden tab retrying every 2.5s forever; a single success
 * resets the counter, so a transient blip never costs the user the live view.
 */
const MAX_POLL_FAILURES = 5;

/**
 * Paused batches are intentionally polled: their worker resumes asynchronously.
 * A cancelled batch is terminal, so polling stops exactly as it does on done/error.
 */
export const shouldPollAffiliateCsvImport = (
  data: ImportBatchFetchResult | undefined,
  fetchFailureCount = 0
): boolean =>
  fetchFailureCount < MAX_POLL_FAILURES &&
  !data?.notFound &&
  !isTerminalImportStatus(data?.progress.status);

const fetchBatch = async (
  programId: string,
  batchId: string,
  signal: AbortSignal
): Promise<ImportBatchFetchResult> => {
  try {
    const response = await api.get<ApiResponse<ImportBatchProgress>>(
      `/dashboard/programs/${programId}/affiliate-imports/${batchId}`,
      { signal }
    );
    if (!response.data.data) {
      return { notFound: true };
    }
    if (response.data.data.batchId !== batchId)
      throw new Error('Unexpected import batch response.');
    return { notFound: false, progress: response.data.data };
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 404) {
      return { notFound: true };
    }
    throw err;
  }
};

export const useAffiliateCsvImportBatchQuery = ({
  programId,
  batchId,
  enabled = true,
}: UseAffiliateCsvImportBatchParams) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: affiliateCsvImportBatchQueryKey(programId, batchId, user?.currentBrandId),
    queryFn: ({ signal }) => fetchBatch(programId, batchId, signal),
    enabled: enabled && Boolean(user?.currentBrandId) && Boolean(programId) && Boolean(batchId),
    retry: 1,
    // Pusher is an acceleration only. A cold tab, missed terminal event, or
    // browser reconnect must still converge from REST.
    refetchInterval: (query) =>
      shouldPollAffiliateCsvImport(query.state.data, query.state.fetchFailureCount) ? 2500 : false,
  });
};
